import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { 
  Heart, Clock, Users, MessageCircle, CheckCircle, Plus, 
  LogOut, Settings, Menu, X, ChevronRight, Calendar,
  AlertCircle, TrendingUp, User, Briefcase, Home, DollarSign, Book, CreditCard,
  UserPlus, Award, CalendarClock, HelpCircle, Pill, Activity, Receipt, Palmtree,
  Shield, BarChart3, Radio, Zap, Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import NotificationCenter from 'components/NotificationCenter';
import AccessibilitySettings from 'components/AccessibilitySettings';
import MobileBottomNav from 'components/MobileBottomNav';
import PullToRefresh from 'components/PullToRefresh';
import { hapticFeedback, hideSplashScreen, initAppListeners } from 'lib/native';


export default function Dashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchDashboard();
    hideSplashScreen();
    initAppListeners(
      (canGoBack) => { if (canGoBack) window.history.back(); },
      (isActive) => { if (isActive) fetchDashboard(); }
    );
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId) => {
    try {
      hapticFeedback('light');
      // Optimistic update
      setDashboardData(prev => {
        if (!prev?.tasks) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t =>
            t.task_id === taskId ? { ...t, completed: !t.completed } : t
          )
        };
      });
      await api.post(`/tasks/${taskId}/complete`, {});
      toast.success('Task updated!');
    } catch (error) {
      // Revert on failure
      fetchDashboard();
      toast.error('Failed to update task');
    }
  };

  const handleLogout = async () => {
    hapticFeedback('medium');
    await logout();
    navigate('/');
  };

  const isCaregiver = user?.role === 'independent_caregiver';

  const navItems = isCaregiver ? [
    { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
    { icon: Clock, label: t('nav.care_hours'), path: '/care-hours' },
    { icon: Zap, label: t('nav.backup_system'), path: '/backup-shifts' },
    // { icon: Stethoscope, label: t('nav.health_alerts'), path: '/health-alerts' },
    // { icon: CreditCard, label: t('nav.payroll'), path: '/payroll' },
    { icon: Award, label: t('nav.certifications'), path: '/certifications' },
    { icon: CalendarClock, label: t('nav.availability'), path: '/availability' },
    { icon: Palmtree, label: t('nav.time_off'), path: '/time-off' },
    { icon: Shield, label: t('nav.evv_compliance'), path: '/evv' },
    { icon: Radio, label: t('nav.shift_monitoring'), path: '/shift-monitoring' },
    { icon: BarChart3, label: t('nav.analytics'), path: '/analytics' },
    { icon: Users, label: 'Client Requests', path: '/clients' },
    { icon: Book, label: t('nav.resources'), path: '/resources' },
    { icon: MessageCircle, label: t('nav.messages'), path: '/messages' },
    { icon: HelpCircle, label: t('nav.support'), path: '/support' },
    { icon: Settings, label: t('common.settings'), path: '/settings' },
  ] : [
    { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
    { icon: MessageCircle, label: t('nav.care_guide'), path: '/care-guide' },
    { icon: Users, label: t('nav.care_circle'), path: '/care-circle' },
    { icon: Stethoscope, label: t('nav.health_alerts'), path: '/health-alerts' },
    { icon: UserPlus, label: t('nav.quick_enroll'), path: '/quick-enroll' },
    { icon: CalendarClock, label: t('nav.team_calendar'), path: '/availability' },
    { icon: Palmtree, label: t('nav.time_off'), path: '/time-off' },
    { icon: Clock, label: t('nav.care_hours'), path: '/care-hours' },
    { icon: Zap, label: t('nav.backup_system'), path: '/backup-shifts' },
    // { icon: DollarSign, label: t('nav.budget'), path: '/budget' },
    { icon: Shield, label: t('nav.evv_compliance'), path: '/evv' },
    { icon: Radio, label: t('nav.shift_monitoring'), path: '/shift-monitoring' },
    { icon: BarChart3, label: t('nav.analytics'), path: '/analytics' },
    { icon: Briefcase, label: t('nav.find_caregivers'), path: '/caregivers' },
    { icon: Book, label: t('nav.resources'), path: '/resources' },
    { icon: Heart, label: t('nav.stress_checkin'), path: '/stress-checkin' },
    { icon: HelpCircle, label: t('nav.support'), path: '/support' },
    { icon: Settings, label: t('common.settings'), path: '/settings' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform lg:translate-x-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`} role="navigation" aria-label="Main navigation">
        <div className="shrink-0 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/images/elderlink-logo.webp" alt="ElderLink" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold text-slate-900 font-['Manrope']">ElderLink</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-500">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 space-y-1 pb-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                window.location.pathname === item.path
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="shrink-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3 p-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg font-bold text-teal-700">{user?.name?.[0] || '?'}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.subscription_tier === 'premium' ? 'Premium' : t('dashboard.free_plan')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600">
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']" data-testid="dashboard-title">
                  {isCaregiver ? t('dashboard.caregiver_title') : t('dashboard.todays_overview')}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500">
                  {t('dashboard.welcome', { name: user?.name?.split(' ')[0] })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AccessibilitySettings />
              <NotificationCenter />
              {!isCaregiver && user?.subscription_tier !== 'premium' && (
                <Link to="/subscription" className="hidden sm:block">
                  <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full text-sm" data-testid="upgrade-btn">
                    Upgrade
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </header>

        {/* Dashboard content */}
        <PullToRefresh onRefresh={fetchDashboard}>
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 mobile-content-pad">
          {/* Quick actions / Setup prompt */}
          {!dashboardData?.profile && !isCaregiver && (
            <Card className="bg-gradient-to-r from-teal-500 to-teal-600 border-0 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold font-['Manrope']">{t('dashboard.complete_profile')}</h3>
                    <p className="text-teal-100 mt-1">{t('dashboard.complete_profile_desc')}</p>
                  </div>
                  <Link to="/care-profile/setup">
                    <Button className="bg-white text-teal-600 hover:bg-teal-50 rounded-full" data-testid="setup-profile-btn">
                      {t('dashboard.setup_profile')}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {isCaregiver ? (
              <>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">{t('dashboard.this_week')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                          {dashboardData?.hours_summary?.week_hours || 0}h
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-teal-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">Active Shift</p>
                        <p className="text-xl font-bold text-slate-900 mt-1">
                          {dashboardData?.active_shift ? 'In Progress' : 'None'}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        dashboardData?.active_shift ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        <Briefcase className={`w-6 h-6 ${dashboardData?.active_shift ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">{t('dashboard.hours_this_week')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                          {dashboardData?.hours_summary?.week_hours || 0}h
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">{t('dashboard.tasks_today')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                          {dashboardData?.tasks?.length || 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">{t('dashboard.caregiver_on_duty')}</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">
                          {dashboardData?.on_duty || 'None'}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        dashboardData?.on_duty ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        <User className={`w-6 h-6 ${dashboardData?.on_duty ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-500 text-sm">{t('dashboard.last_stress_check')}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                          {dashboardData?.latest_stress?.stress_level || '-'}/5
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        dashboardData?.latest_stress?.stress_level >= 4 ? 'bg-rose-100' : 'bg-teal-100'
                      }`}>
                        <Heart className={`w-6 h-6 ${
                          dashboardData?.latest_stress?.stress_level >= 4 ? 'text-rose-600' : 'text-teal-600'
                        }`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Main Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Tasks / Quick Actions */}
            <div className="lg:col-span-2 space-y-6">
              {!isCaregiver && (
                <>
                  {/* Quick Action Buttons */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
                    <Link to="/care-guide" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-3 sm:p-4 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-teal-100 flex items-center justify-center mx-auto mb-2">
                            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.ai_guide')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/stress-checkin" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-3 sm:p-4 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-rose-100 flex items-center justify-center mx-auto mb-2">
                            <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-rose-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.checkin')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/care-circle" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-3 sm:p-4 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.care_circle')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/messages" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-3 sm:p-4 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-2">
                            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.messages')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    {!isCaregiver ? (
                      <Link to="/caregivers" className="block">
                        <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                          <CardContent className="p-3 sm:p-4 text-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                              <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                            <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.find_caregivers')}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ) : (
                      <Link to="/clients" className="block">
                        <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                          <CardContent className="p-3 sm:p-4 text-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                              <Home className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                            <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">Client Requests</p>
                          </CardContent>
                        </Card>
                      </Link>
                    )}
                  </div>

                  {/* Health & Finance Quick Actions */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <Link to="/medications" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-3 sm:p-4 text-center">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-2">
                            <Pill className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-xs sm:text-sm leading-tight">{t('dashboard.medications')}</p>
                        </CardContent>
                      </Card>
                    </Link>
                    {/* <Link to="/health-metrics" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-4 text-center">
                          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mx-auto mb-2">
                            <Activity className="w-6 h-6 text-red-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-sm">{t('dashboard.health_metrics')}</p>
                        </CardContent>
                      </Card>
                    </Link> */}
                    {/* <Link to="/expenses" className="block">
                      <Card className="hover:shadow-md transition-shadow cursor-pointer card-interactive h-full">
                        <CardContent className="p-4 text-center">
                          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                            <Receipt className="w-6 h-6 text-emerald-600" />
                          </div>
                          <p className="font-medium text-slate-900 text-sm">{t('dashboard.expenses')}</p>
                        </CardContent>
                      </Card>
                    </Link> */}
                  </div>

                  {/* Today's Tasks */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-lg font-semibold font-['Manrope']">{t('dashboard.daily_checklist')}</CardTitle>
                      <Link to="/daily-checklist">
                        <Button variant="ghost" size="sm" className="text-teal-600">
                          {t('dashboard.view_all')}
                        </Button>
                      </Link>
                    </CardHeader>
                    <CardContent>
                      {dashboardData?.tasks?.length > 0 ? (
                        <div className="space-y-3">
                          {dashboardData.tasks.slice(0, 5).map((task) => (
                            <button
                              key={task.task_id}
                              onClick={() => handleToggleTask(task.task_id)}
                              className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl w-full text-left hover:bg-slate-100 transition-colors group"
                              data-testid={`dashboard-task-${task.task_id}`}
                              aria-label={`${task.completed ? 'Reopen' : 'Complete'} task: ${task.title}`}
                            >
                              <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 transition-colors ${
                                task.completed
                                  ? 'bg-teal-500 border-teal-500'
                                  : 'border-slate-300 group-hover:border-teal-400'
                              } flex items-center justify-center`}>
                                {task.completed && <CheckCircle className="w-4 h-4 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${task.completed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                                  {task.title}
                                </p>
                                {task.due_date && (
                                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(task.due_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                          <p className="text-slate-500">{t('dashboard.no_tasks')}</p>
                          <Link to="/daily-checklist">
                            <Button variant="outline" size="sm" className="mt-3">
                              <Plus className="w-4 h-4" />
                              {t('dashboard.add_task')}
                            </Button>
                          </Link>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Caregiver: Clock In/Out */}
              {isCaregiver && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Link to="/care-hours" className="block">
                      <Button className="w-full h-14 sm:h-16 text-base sm:text-lg bg-teal-500 hover:bg-teal-600 text-white rounded-xl" data-testid="clock-in-btn">
                        <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
                        {dashboardData?.active_shift ? 'View Active Shift' : 'Start New Shift'}
                      </Button>
                    </Link>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <Link to="/messages" className="block">
                        <Button variant="outline" className="w-full h-10 sm:h-12 rounded-xl text-xs sm:text-sm">
                          <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                          Messages
                        </Button>
                      </Link>
                      <Link to="/settings" className="block">
                        <Button variant="outline" className="w-full h-10 sm:h-12 rounded-xl text-xs sm:text-sm">
                          <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                          Profile
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Activity Feed / Care Profile */}
            <div className="space-y-6">
              {/* Care Profile Card */}
              {dashboardData?.profile && !isCaregiver && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">{t('dashboard.care_recipient')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                        {user?.picture ? (
                          <img src={user.picture} alt={dashboardData.profile.care_recipient_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl font-bold text-teal-700">
                            {dashboardData.profile.care_recipient_name?.[0] || '?'}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{dashboardData.profile.care_recipient_name}</h3>
                        <p className="text-sm text-slate-500">{dashboardData.profile.age} {t('dashboard.years_old')}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {dashboardData.profile.conditions?.slice(0, 3).map((condition, i) => (
                        <Badge key={i} variant="secondary" className="mr-1">
                          {condition}
                        </Badge>
                      ))}
                    </div>
                    <Link to="/care-profile/setup">
                      <Button variant="ghost" className="w-full mt-4 text-teal-600">
                        {t('dashboard.edit_profile')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {/* Activity Feed */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope']">{t('dashboard.recent_activity')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.activity?.length > 0 ? (
                    <div className="space-y-4">
                      {dashboardData.activity.slice(0, 5).map((activity, index) => (
                        <div key={activity.activity_id || index} className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            {activity.action === 'clocked_in' ? (
                              <Clock className="w-4 h-4 text-green-600" />
                            ) : activity.action === 'clocked_out' ? (
                              <Clock className="w-4 h-4 text-slate-600" />
                            ) : activity.action === 'completed_task' ? (
                              <CheckCircle className="w-4 h-4 text-teal-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900">
                              <span className="font-medium">{activity.user_name}</span>
                              {' '}
                              {activity.action === 'clocked_in' ? 'clocked in' :
                               activity.action === 'clocked_out' ? `clocked out (${activity.target})` :
                               activity.action === 'completed_task' ? `completed "${activity.target}"` :
                               activity.action === 'created_task' ? `added task "${activity.target}"` :
                               activity.action === 'invited' ? `invited ${activity.target}` :
                               activity.message || activity.action || activity.type || 'performed an action'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(activity.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <TrendingUp className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">No recent activity</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        </PullToRefresh>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}
